import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { adminApi, type AgentPersonaRow } from '../../api/adminApi';
import { Badge, Button, DataTable, type ColumnDef, type SortState } from '@shared-ui/components';
import { PersonaForm } from './PersonaForm';
import { deviceLabel } from './deviceLabel';

// Sort keys → a comparable value per persona (null = no value, sinks to the bottom).
const SORT_ACCESSORS: Record<string, (p: AgentPersonaRow) => number | string | null> = {
  persona: p => p.personaId.toLowerCase(),
  role: p => (p.spec?.role ?? '').toLowerCase(),
  board: p => (p.spec?.board ?? '').toLowerCase(),
  grade: p => (p.spec?.grade ?? null),
  device: p => deviceLabel(p.spec?.device),
  runs: p => (p.runCount ?? 0),
  first: p => (p.firstRunAt ? new Date(p.firstRunAt).getTime() : null),
  last: p => (p.lastRunAt ? new Date(p.lastRunAt).getTime() : null),
  findings: p => (p.findingsCount ?? 0),
};

export function PersonasTab() {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ sortBy: 'persona', sortOrder: 'asc' });
  const { data, isLoading } = useQuery({ queryKey: ['agent-personas'], queryFn: () => adminApi.getAgentPersonas() });
  const rows = data?.personas ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? rows : rows.filter(p => {
      const s = p.spec;
      const hay = [p.personaId, s?.role, s?.board, s?.grade, s?.subjects?.join(' '), deviceLabel(s?.device)]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    const acc = SORT_ACCESSORS[sort.sortBy];
    if (!acc) return base;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;   // nulls sink regardless of direction
      if (vb === null) return -1;
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [rows, search, sort]);

  const columns = useMemo<ColumnDef<AgentPersonaRow>[]>(() => [
    { key: 'persona', header: 'Persona', sortable: true, render: p => <span className="pa-mono">{p.personaId}</span> },
    { key: 'role', header: 'Role', sortable: true, render: p => p.spec?.role ?? '—' },
    { key: 'board', header: 'Board / Exam', sortable: true, render: p => (p.spec?.board ? <Badge variant="default" size="sm">{p.spec.board}</Badge> : '—') },
    { key: 'grade', header: 'Grade', sortable: true, className: 'pa-num', render: p => (p.spec?.grade != null ? p.spec.grade : '—') },
    { key: 'device', header: 'Device', sortable: true, render: p => <Badge variant="default" size="sm">{deviceLabel(p.spec?.device)}</Badge> },
    { key: 'runs', header: 'Runs', sortable: true, className: 'pa-num', render: p => p.runCount || '—' },
    { key: 'first', header: 'First run', sortable: true, className: 'pa-num', render: p => (p.firstRunAt ? format(new Date(p.firstRunAt), 'MMM d') : '—') },
    { key: 'last', header: 'Last run', sortable: true, className: 'pa-num', render: p => (p.lastRunAt ? format(new Date(p.lastRunAt), 'MMM d HH:mm') : '—') },
    { key: 'findings', header: 'Findings', sortable: true, className: 'pa-num', render: p => p.findingsCount || '—' },
  ], []);

  return (
    <>
      <div className="pa-filters">
        <input
          className="pa-input pa-search"
          type="search"
          placeholder="Search persona, role, board, grade…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Button variant="primary" size="sm" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>+ Add persona</Button>
      </div>
      <DataTable<AgentPersonaRow>
        columns={columns}
        data={filtered}
        rowKey={p => p.personaId}
        sort={sort}
        onSortChange={setSort}
        onRowClick={p => navigate(`/persona-agents/personas/${encodeURIComponent(p.personaId)}`)}
        loading={isLoading}
        emptyMessage={search ? 'No personas match your search' : 'No personas yet — add one'}
      />
      {adding && <PersonaForm onClose={() => setAdding(false)} />}
    </>
  );
}
